import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '../cn';
import Button from '../Input2/Button';
import { General, Media } from '../StrokeIcons';

export interface VoiceRecorderProps {
  onRecordingComplete: (audioBlob: Blob, durationMs: number) => void;
  isUploading?: boolean;
  disabled?: boolean;
  className?: string;
  /** Auto-start recording when component mounts */
  autoStart?: boolean;
  /** Cancel/back out of recording entirely. Releases the mic and exits WITHOUT delivering audio. */
  onCancel?: () => void;
}

// Number of bars in the waveform
const WAVEFORM_BARS = 32;

export function VoiceRecorder({
  onRecordingComplete,
  isUploading = false,
  disabled = false,
  className,
  autoStart = false,
  onCancel,
}: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [waveformData, setWaveformData] = useState<number[]>(Array.from({ length: WAVEFORM_BARS }, () => 0));

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const recordingTimeRef = useRef(0);
  const recordingStartTimeRef = useRef<number>(0);
  const hasAutoStarted = useRef(false);
  // When true, the next `onstop` releases the mic but does NOT deliver audio (cancel / unmount).
  const isCancellingRef = useRef(false);

  const updateWaveform = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Sample the frequency data to create waveform bars
    const step = Math.floor(dataArray.length / WAVEFORM_BARS);
    const newWaveform: number[] = [];
    for (let i = 0; i < WAVEFORM_BARS; i++) {
      // Take average of a range for smoother visualization
      let sum = 0;
      for (let j = 0; j < step; j++) {
        sum += dataArray[i * step + j] || 0;
      }
      newWaveform.push(sum / step / 255); // Normalize to 0-1
    }
    setWaveformData(newWaveform);

    if (mediaRecorderRef.current?.state === 'recording') {
      animationFrameRef.current = requestAnimationFrame(updateWaveform);
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up audio analysis for waveform
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Prefer webm for better compression, fallback to other formats
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Always release the mic, regardless of stop vs cancel.
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        analyserRef.current = null;
        setIsRecording(false);
        setRecordingTime(0);
        recordingTimeRef.current = 0;
        setWaveformData(Array.from({ length: WAVEFORM_BARS }, () => 0));

        // Cancelled (or unmounted mid-recording): release the mic but don't deliver audio.
        if (isCancellingRef.current) {
          isCancellingRef.current = false;
          return;
        }

        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const actualDurationMs = performance.now() - recordingStartTimeRef.current;
        onRecordingComplete(blob, actualDurationMs);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimeRef.current = 0;
      recordingStartTimeRef.current = performance.now();

      // Start timer
      timerRef.current = setInterval(() => {
        recordingTimeRef.current += 1;
        setRecordingTime(recordingTimeRef.current);
      }, 1000);

      // Start waveform monitoring
      animationFrameRef.current = requestAnimationFrame(updateWaveform);
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Microphone access denied. Please allow microphone access and try again.');
    }
  }, [onRecordingComplete, updateWaveform]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Discard the current take: release the mic and exit WITHOUT delivering audio. Only sets the
  // cancel flag when there's an active recorder to stop — otherwise `onstop` never fires and the
  // flag would leak into the next real recording.
  const handleCancel = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      isCancellingRef.current = true;
      recorder.stop(); // onstop releases the mic and skips onRecordingComplete
    } else if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    setRecordingTime(0);
    recordingTimeRef.current = 0;
    setWaveformData(Array.from({ length: WAVEFORM_BARS }, () => 0));
    setError(null);
    onCancel?.();
  }, [onCancel]);

  // Auto-start recording when component mounts if autoStart is true
  useEffect(() => {
    if (autoStart && !hasAutoStarted.current && !isRecording && !disabled && !isUploading) {
      hasAutoStarted.current = true;
      void startRecording();
    }
  }, [autoStart, isRecording, disabled, isUploading, startRecording]);

  // Cleanup on unmount — release the mic without delivering a spurious recording (the old code
  // called stopRecording(), which fired onRecordingComplete with whatever had been captured).
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        isCancellingRef.current = true;
        recorder.stop();
      } else if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3 w-full', className)}>
        <p className="text-sm text-red-500 text-center max-w-xs">{error}</p>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setError(null);
              void startRecording();
            }}
            color="red"
            icon={<Media.Microphone01 />}>
            Try again
          </Button>
          {onCancel && (
            <Button onClick={handleCancel} color="gray" text icon={<General.XMark />}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Idle state - show start recording button
  if (!isRecording) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3 w-full', className)}>
        <Button
          onClick={() => void startRecording()}
          disabled={disabled || isUploading}
          color="red"
          size="lg"
          icon={<Media.Microphone01 />}>
          Start Recording
        </Button>
        {onCancel && (
          <Button onClick={handleCancel} color="gray" size="sm" text icon={<General.XMark />}>
            Cancel
          </Button>
        )}
      </div>
    );
  }

  // Recording state - show waveform and stop button
  return (
    <div className={cn('flex flex-col items-center justify-center gap-4 w-full h-full px-6', className)}>
      {/* Timer */}
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-lg font-semibold text-red-500 tabular-nums">{formatTime(recordingTime)}</span>
      </div>

      {/* Waveform visualization */}
      <div className="flex items-center justify-center gap-0.5 h-16 w-full max-w-md">
        {waveformData.map((level, i) => (
          <div
            key={i}
            className="flex-1 bg-red-400 rounded-full transition-all duration-75"
            style={{
              height: `${Math.max(4, level * 100)}%`,
              opacity: 0.4 + level * 0.6,
            }}
          />
        ))}
      </div>

      {/* Stop (keep) + Cancel (discard) */}
      <div className="flex items-center gap-2">
        <Button onClick={stopRecording} color="red" size="lg" icon={<Media.PauseSquare />}>
          Stop Recording
        </Button>
        {onCancel && (
          <Button
            onClick={handleCancel}
            color="gray"
            size="lg"
            text
            tooltip="Discard this recording and exit"
            icon={<General.XMark />}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
